"use client";

import { useEffect, useMemo, useState } from "react";
import { Item } from "@/utils/itemclass";
import { useItemList } from "@/hooks/useItemList";
import AppCheckbox from "@/ui/components/Checkbox";
import FilterBar from "@/ui/components/FilterBar";
import PageHeader from "@/ui/components/PageHeader";
import ProfitTable from "@/ui/components/ProfitTable";
import type { AlchemyBuffsMarketData } from "@/types/AlchemyTypes";
import Coins from "@/ui/components/Coins";

export default function CoinifyPage() {
  const [filter, setFilter] = useState("");
  const { entries } = useItemList("/api/items/list");
  const [useCatalystTea, setUseCatalystTea] = useState(false);
  const [catalystTeaSellPrice, setCatalystTeaSellPrice] = useState(NaN);
  const [useCoc, setUseCoc] = useState(false);
  const [cocPrice, setCocPrice] = useState(NaN);

  async function loadAlchemyBuffsSellPrice() {
    const res = await fetch("/api/market/alchemy_buffs");
    if (!res.ok) {
      return;
    }
    const data: AlchemyBuffsMarketData = await res.json();

    setCatalystTeaSellPrice(data.teaSell);
    setCocPrice(data.cocSell);
  }

  useEffect(() => {
    loadAlchemyBuffsSellPrice();
  }, []);

  const filteredAndSorted = useMemo(() => {
    const sorted = [...entries].sort(
      (a, b) =>
        b.getCoinifyProfit({
          useCatalystTea: useCatalystTea,
          catalystTeaPrice: catalystTeaSellPrice,
          useCoc: useCoc,
          cocPrice: cocPrice,
        }) -
        a.getCoinifyProfit({
          useCatalystTea: useCatalystTea,
          catalystTeaPrice: catalystTeaSellPrice,
          useCoc: useCoc,
          cocPrice: cocPrice,
        }),
    );

    if (filter.trim() === "") return sorted;

    return sorted.filter((entry) =>
      entry.name.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [entries, filter, useCatalystTea, catalystTeaSellPrice, useCoc, cocPrice]);

  return (
    <div className="mt-4 px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Coinify Profit Calculator"
        description="This list shows the potential profits for each item when purchased from the marketplace and converted into coins using the Alchemy skill."
      />
      {/* FILTER */}
      <FilterBar
        value={filter}
        onChange={setFilter}
        datalistEntries={entries.map((entry) => ({
          key: entry.item,
          label: entry.name,
        }))}
        showSortButton={false}
      />

      {/** OPTIONS */}

      <div>
        <AppCheckbox
          id="catalyst_tea"
          label="Catalyst Tea"
          description="+5% multiplicative bonus to success rate while alchemizing"
          checked={useCatalystTea}
          onChange={setUseCatalystTea}
        />
      </div>
      <div>
        <AppCheckbox
          id="catalyst_of_coinification"
          label="Catalyst of Coinification"
          description="increase the coinify success rate by +15%"
          checked={useCoc}
          onChange={setUseCoc}
        />
      </div>

      {/** TABLE */}
      <ProfitTable
        columns={["Item", "Profit per 10 seconds", "Profit Threshold (Buy)"]}
      >
        {filteredAndSorted.map((item: Item) => (
          <tr key={item.item}>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray">
              {item.name}
            </td>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
              <Coins
                value={item.getCoinifyProfit({
                  useCatalystTea: useCatalystTea,
                  catalystTeaPrice: catalystTeaSellPrice,
                  useCoc: useCoc,
                  cocPrice: cocPrice,
                })}
              />
            </td>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
              <Coins
                value={item.getCoinifyProfitBuyThreshold({
                  useCatalystTea: useCatalystTea,
                  catalystTeaPrice: catalystTeaSellPrice,
                  useCoc: useCoc,
                  cocPrice: cocPrice,
                })}
              />
            </td>
          </tr>
        ))}
      </ProfitTable>
    </div>
  );
}
